import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import BootstrapTable from "react-bootstrap-table-next";
import {
  API,
  defaultLimitPagination,
  get,
  historyTypeFilterOPtions,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { useOrganization } from "../../lib/context/OrganisationContext";
import Spin from "../../components/Spin";
import { formatName, getModuleNamingBySlug } from "../../lib/Misc";
import moment from "moment";
import { Clock, FileStack, Shield, User } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Pagination } from "react-bootstrap";
import { AnimatedTooltip } from "../../components/AnimatedTooltip";
import { Label } from "../../components/ui/label";
import CustomLabelText from "../../components/CustomLabelText";
import SearchSelect from "../../components/SearchSelect";

const TokenHistory = ({ defaultType = "" }) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const { organizationData } = useOrganization();
  const [type, setType] = useState(defaultType);

  const toggleShowHistory = () => {
    setShowHistory(!showHistory);
  };

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "created_at",
      text: "Date & Time",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "activity",
      text: "Activity",
    },

    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "contact_type",
      text: "Contact Type",
    },

    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
    },

    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "type",
      text: "Type",
    },

    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "user",
      text: "Used By",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "tokens",
      text: "Tokens Used",
      headerAlign: (column, colIndex) => "right",
      align: "right",
    },
  ];

  const fetchTokenHistory = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_TOKEN_HISTORY}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&organization_id=${
            organizationData?.organizationId
              ? organizationData?.organizationId
              : ""
          }&type=${type ? type : ''}`,
        );
        setLoading(false);
        const history = data.data.data;
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        setHistoryData(history);
        return data.data;
      } catch (error) {
        setHistoryData([]);
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationData, type],
  );

  useEffect(() => {
    fetchTokenHistory();
  }, [fetchTokenHistory]);

  const tokenHistoryGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      let contact = historyData[i]?.kyc?.beneficiary_id
        ? historyData[i]?.kyc?.beneficiary
        : historyData[i]?.kyc?.vendor_id
          ? historyData[i]?.kyc?.vendor
          : historyData[i]?.kyc?.contact_id
            ? historyData[i]?.kyc?.contact
            : "";

      const userName = historyData[i]?.user?.first_name
        ? `${historyData[i]?.user?.first_name} ${
            historyData[i]?.user?.last_name
              ? historyData[i]?.user?.last_name
              : ""
          }`
        : historyData[i]?.user?.last_name;

      items.push({
        key: historyData[i]?.id,
        id: historyData[i]?.id ? historyData[i]?.id : "",

        contact_type: historyData[i]?.kyc?.beneficiary_id
          ? "Beneficiary"
          : historyData[i]?.kyc?.vendor_id
            ? getModuleNamingBySlug(
                organizationData?.organizationData,
                "vendors",
                "Vendor",
              )
            : historyData[i]?.kyc?.contact_id
              ? getModuleNamingBySlug(
                  organizationData?.organizationData,
                  "contacts",
                  "Customer",
                )
              : "",

        type: historyData[i]?.type === 1 ? "KYC" : "KYB",

        created_at: (
          <div>
            <p>
              {moment(historyData[i]?.created_at, "DD-MM-YYYY HH:mm:ss").format(
                "DD MMM YYYY",
              )}
            </p>

            <div className="flex items-center gap-1 text-muted-foreground text-sm">
              <Clock size={14} />
              {moment(historyData[i]?.created_at, "DD-MM-YYYY HH:mm:ss").format(
                "hh:mm A",
              )}
            </div>
          </div>
        ),

        activity: (
          <div className="flex items-center gap-3">
            <Shield size={20} />

            <div>
              <p>Token used for <strong>{historyData[i]?.type === 1 ? "KYC" : "KYB"}</strong> verification</p>

              <div className="text-muted-foreground text-sm">
                Source: {historyData[i]?.used_on || "-"}
              </div>
            </div>
          </div>
        ),

        name: (
          <div>
            <p>
              {formatName(contact?.first_name, contact?.last_name) +
                (contact?.business_name ? ` (${contact?.business_name})` : "")}
            </p>

            <div className="text-muted-foreground text-sm">
              Code: {contact?.code ? contact?.code : ""}
            </div>
          </div>
        ),

        user: (
          <div className="flex items-center gap-3">
            <User size={20} />

            <div>
              <p>{userName || "-"}</p>

              {historyData[i]?.user?.email && (
                <div className="text-muted-foreground text-sm">
                  {historyData[i]?.user.email}
                </div>
              )}
            </div>
          </div>
        ),

        tokens: <strong>1</strong>,
      });
    }
    return items;
  };

  const historys = tokenHistoryGenerator(historyData?.length);

  /**
   * * Start: History List Change Limit
   * @param {Event} e (Limit Number)
   **/
  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchTokenHistory(limit, 1);
  };

  //* End: History List Change Limit

  /**
   * * Start: History List Change Page
   * @param {Number} page (Page Number)
   **/
  const handleChangePage = (page) => {
    setPage(page);
    fetchTokenHistory(limit, page);
  };

  return (
    <>
      <AnimatedTooltip title={"Token History"}>
        <Button size={"icon"} onClick={toggleShowHistory}>
          <FileStack size={14} />
        </Button>
      </AnimatedTooltip>

      <ResponsiveDrawerForm
        description={""}
        title={"Token History"}
        handleOpen={() => {}}
        handleClose={() => {
          setShowHistory(false);
        }}
        className={"md:w-[90vw]"}
        open={showHistory}
        buttonClassName={""}
        setOpen={setShowHistory}
        read={true}
      >
        <div className="relative">
          {loading && (
            <Spin expand={true} smallModal={true} fullscreen={true} />
          )}

          <div className="absolute -top-12 right-5">
            <div className="grid gap-2">
              <div className="min-w-0 flex items-center gap-2">
                <Label htmlFor="aml_ctf_contact_id">
                  <CustomLabelText labelText="Filter by:" errorField={""} />
                </Label>
                <div className="relative">
                  <SearchSelect
                    frameworks={historyTypeFilterOPtions}
                    optionType={"Filter by"}
                    handleSelect={(e) => {
                      setType(e);
                    }}
                    clearable={true}
                    initialValue={type ? type : ""}
                    onClear={() => {
                      setType(null);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="custom-table">
            <div className={"table-body-class w-full"}>
              <BootstrapTable
                keyField="key"
                data={historys}
                columns={columns}
                noDataIndication="No Data Found"
                rowClasses={TRClasses}
                classes={TableClasses}
                wrapperClasses={TableWrapClasses}
              />
            </div>
            <Pagination
              total={total}
              limit={parseInt(limit)}
              currentPage={page}
              updateLimit={handleUpdateLimit}
              updatePage={handleChangePage}
              from={from}
              to={to}
            />
          </div>
        </div>
      </ResponsiveDrawerForm>
    </>
  );
};

export default TokenHistory;
